import React, { useState, useEffect, useRef } from 'react';
import { useWindowEvent } from '../../../../shared/hooks/useWindowEvent';
import { createPortal } from 'react-dom';
import { MessageCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './FloatingCommentsPanel.css';

// frontend-restructuring Fase 3 (03.1-auth-store.md §6 grupo 1) -- presentacional, `user`
// llega por prop de su contenedor (DoctexHtml.jsx) en vez de leer `useUser()` directo.
// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: resuelve el contenedor de portal vía DOM, auto-expande al crear un comentario borrador y dispara el fetch de comentarios al tener `docId`.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const FloatingCommentsPanel = ({ docId, user, draftComment, onDraftSubmit, onDraftCancel, onHighlightClick }) => {
    const { t } = useTranslation('docs');
    const [comments, setComments] = useState([]);
    const [draftText, setDraftText] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [menu2Container, setMenu2Container] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        setMenu2Container(document.querySelector('.menu2-container'));
    }, []);

    useEffect(() => {
        if (draftComment) {
            setIsExpanded(true);
        }
    }, [draftComment]);

    const fetchComments = async () => {
        try {
            const res = await fetch(`/api/comments/${docId}`);
            const data = await res.json();
            if (data.success) {
                setComments(data.comments || []);
            }
        } catch (e) {
            console.error('Error fetching comments:', e);
        }
    };

    const [positions, setPositions] = useState({});
    const [editorTop, setEditorTop] = useState(20);

    const updatePositions = () => {
        const editor = document.querySelector('.doctex-rich-editor');
        const wrapper = document.querySelector('.doctex-html-wrapper');
        const menu2 = document.querySelector('.menu2-container');
        if (!editor || !wrapper || !menu2) return;

        const wrapperRect = wrapper.getBoundingClientRect();
        const menu2Rect = menu2.getBoundingClientRect();
        
        // editorTop is the offset of the wrapper from the top of menu2
        setEditorTop(Math.max(0, wrapperRect.top - menu2Rect.top));

        const newPositions = {};
        const highlights = editor.querySelectorAll('.comment-highlight');

        highlights.forEach(span => {
            const id = span.getAttribute('data-comment-id');
            if (id) {
                const rect = span.getBoundingClientRect();
                // Position relative to the wrapper
                const yPosition = rect.top - wrapperRect.top;
                newPositions[id] = yPosition;
            }
        });
        setPositions(newPositions);
    };

    useEffect(() => {
        if (docId) {
            fetchComments();
        }
    }, [docId]);

    // 04-enforce.md 4.3 -- el `resize` de `window` va por la suscripción compartida (el scroll
    // sigue local porque es sobre `.doc-content-container`, no sobre `window`).
    useWindowEvent('resize', updatePositions);

    useEffect(() => {
        // Initial update
        // We delay it slightly to ensure DOM is ready
        setTimeout(updatePositions, 100);

        // Setup MutationObserver to watch for changes in the editor
        // This ensures positions update when markdown finishes loading
        let observer = null;
        const editor = document.querySelector('.doctex-rich-editor');
        if (editor) {
            observer = new MutationObserver(() => {
                updatePositions();
            });
            observer.observe(editor, { childList: true, subtree: true });
        }
        
        // Listen to scroll to update positions if necessary
        const scrollContainer = document.querySelector('.doc-content-container');
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', updatePositions);
        }
        
        return () => {
            if (observer) observer.disconnect();
            if (scrollContainer) scrollContainer.removeEventListener('scroll', updatePositions);
        };
    }, [comments, isExpanded]);

    // Auto-expand when a new comment is being created
    useEffect(() => {
        if (draftComment) {
            setIsExpanded(true);
        }
    }, [draftComment]);

    const handleDraftKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (draftText.trim()) {
                submitDraft();
            }
        } else if (e.key === 'Escape') {
            onDraftCancel();
            setDraftText('');
        }
    };

    const submitDraft = async () => {
        if (!draftText.trim()) return;
        
        try {
            const payload = {
                document_id: docId,
                author_id: user.id,
                content: draftText,
                anchor_type: 'text_highlight',
                metadata: {
                    anchorId: draftComment.id
                }
            };

            const res = await fetch('/api/comments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (data.success) {
                setDraftText('');
                onDraftSubmit(draftComment.id, data.comment.comment_id);
                fetchComments();
            }
        } catch (e) {
            console.error('Error submitting comment:', e);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const renderComment = (c, isLinked = false, extraStyle = {}) => (
        <div 
            key={c.comment_id || c.id} 
            className={`floating-comment-item ${isLinked ? 'linked' : ''}`}
            style={extraStyle}
        >
            <div className="floating-comment-header">
                <div className="floating-comment-avatar-wrapper">
                    <img
                        src={c.author_avatar || 'https://ui-avatars.com/api/?name=' + (c.author_name || 'U')}
                        alt={t('comments.altAuthor')}
                        className="floating-comment-avatar"
                        loading="lazy"
                    />
                    <div className="floating-comment-icon"><MessageCircle size={8} /></div>
                </div>
                <span className="floating-comment-author">{c.author_name || c.autor || t('comments.defaultAuthor')}</span>
                <span className="floating-comment-date">{formatDate(c.created_at)}</span>
            </div>
            <div className="floating-comment-content" dangerouslySetInnerHTML={{ __html: c.content || '' }} />
        </div>
    );

    const linkedComments = comments.filter(c => c.metadata?.anchorId);
    const unlinkedComments = comments.filter(c => !c.metadata?.anchorId);
    
    // Get unique avatars up to 6 for unlinked stack
    const uniqueAvatars = [];
    const seen = new Set();
    for (const c of unlinkedComments) {
        const avatar = c.author_avatar || 'https://ui-avatars.com/api/?name=' + (c.author_name || 'U');
        if (!seen.has(avatar)) {
            seen.add(avatar);
            uniqueAvatars.push(avatar);
            if (uniqueAvatars.length >= 6) break;
        }
    }

    // Calculate positions and cluster by vertical proximity (e.g. same paragraph)
    const PROXIMITY_THRESHOLD = 40; // Pixels
    
    // Sort all linked comments by their vertical position
    const sortedLinked = [...linkedComments].sort((a, b) => 
        (positions[a.metadata.anchorId] || 0) - (positions[b.metadata.anchorId] || 0)
    );

    const clusteredComments = []; // Array of clusters: { y: averageY, comments: [] }
    sortedLinked.forEach(c => {
        const y = positions[c.metadata.anchorId] || 0;
        
        // Find if it belongs to the last cluster
        const lastCluster = clusteredComments[clusteredComments.length - 1];
        if (lastCluster && Math.abs(lastCluster.originalY - y) < PROXIMITY_THRESHOLD) {
            // Add to existing cluster
            lastCluster.comments.push(c);
            // We keep the originalY of the first item to anchor the cluster
        } else {
            // Create new cluster
            clusteredComments.push({
                originalY: y,
                comments: [c],
                clusterId: c.metadata.anchorId // use the first anchorId as the key
            });
        }
    });

    // Ensure clusters don't overlap with each other visually
    const anchorPositions = {};
    let lastClusterY = -40; // Prevent overlapping stacks
    
    clusteredComments.forEach(cluster => {
        let targetY = cluster.originalY;
        if (targetY < lastClusterY + 40) {
            targetY = lastClusterY + 40;
        }
        anchorPositions[cluster.clusterId] = targetY;
        lastClusterY = targetY;
    });

    const avatarsColumn = (
        <div className="avatars-column-container" style={{ position: 'absolute', top: 0, right: '-45px', width: '32px', height: '100%', pointerEvents: 'none', zIndex: 40 }}>
            {isExpanded && (
                <div 
                    className="avatar-close-btn" 
                    onClick={() => setIsExpanded(false)}
                    style={{ position: 'sticky', top: '20px', backgroundColor: '#fff', color: '#000', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', pointerEvents: 'auto', zIndex: 60, boxShadow: '0 2px 4px rgba(0,0,0,0.3)', marginBottom: 'var(--space-3)' }}
                >
                    <X size={18} />
                </div>
            )}
            
            {unlinkedComments.length > 0 && (
                <div 
                    className="avatar-stack-container" 
                    onClick={() => setIsExpanded(true)} 
                    style={{ position: 'absolute', top: '50px', right: 0, pointerEvents: 'auto' }}
                >
                    {uniqueAvatars.length > 0 ? (
                        uniqueAvatars.map((src, i) => (
                            <img
                                key={i}
                                src={src}
                                alt={t('comments.altCommenter')}
                                className="avatar-stack-img"
                                style={{ zIndex: uniqueAvatars.length - i }}
                                loading="lazy"
                            />
                        ))
                    ) : (
                        <div className="avatar-stack-empty" style={{ backgroundColor: '#facc15', color: '#000', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MessageCircle size={18} />
                        </div>
                    )}
                    <div className="avatar-stack-count">{unlinkedComments.length}</div>
                </div>
            )}
            
            {clusteredComments.map(cluster => {
                const group = cluster.comments;
                const anchorId = cluster.clusterId;
                const y = anchorPositions[anchorId];
                
                // Get unique avatars for this stack
                const stackAvatars = [];
                const seenStack = new Set();
                for (const c of group) {
                    const avatar = c.author_avatar || 'https://ui-avatars.com/api/?name=' + (c.author_name || 'U');
                    if (!seenStack.has(avatar)) {
                        seenStack.add(avatar);
                        stackAvatars.push(avatar);
                        if (stackAvatars.length >= 6) break;
                    }
                }
                
                return (
                    <div 
                        key={anchorId}
                        className="avatar-stack-container" 
                        style={{ top: `${y}px`, position: 'absolute', right: 0, pointerEvents: 'auto', cursor: 'pointer' }}
                        onClick={() => {
                            setIsExpanded(true);
                            setTimeout(() => {
                                const el = document.getElementById(`comment-card-${group[0].comment_id}`);
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 100);
                        }}
                    >
                        {stackAvatars.map((src, i) => (
                            <img
                                key={i}
                                src={src}
                                alt={t('comments.altCommenter')}
                                className="avatar-stack-img"
                                style={{ zIndex: stackAvatars.length - i }}
                                loading="lazy"
                            />
                        ))}
                        <div className="avatar-stack-count" style={{ backgroundColor: '#facc15', color: '#000' }}>{group.length}</div>
                    </div>
                );
            })}
        </div>
    );

    const expandedPanel = isExpanded ? createPortal(
        <div className="floating-comments-container" ref={scrollRef}>
            {/* Natural flex layout to avoid overlapping */}
            <div className="floating-comments-list" style={{ paddingTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {unlinkedComments.map(c => (
                    <div id={`comment-card-${c.comment_id}`} key={c.comment_id}>
                        {renderComment(c, false)}
                    </div>
                ))}

                {linkedComments.map(c => (
                    <div id={`comment-card-${c.comment_id}`} key={c.comment_id}>
                        {renderComment(c, true)}
                    </div>
                ))}

                {draftComment && (
                    <div 
                        className="floating-comment-input-wrapper"
                        style={{ position: 'relative', marginTop: 'var(--space-3)', left: 0 }}
                    >
                        <div className="floating-comment-avatar-wrapper">
                            <img
                                src={user?.['link-profile-img'] || 'https://ui-avatars.com/api/?name=U'}
                                alt={t('comments.altYou')}
                                className="floating-comment-avatar"
                            />
                            <div className="floating-comment-icon"><MessageCircle size={8} /></div>
                        </div>
                        <div style={{ flex: 1 }}>
                            <textarea
                                className="floating-comment-textarea"
                                placeholder={t('comments.placeholder')}
                                value={draftText}
                                onChange={(e) => setDraftText(e.target.value)}
                                onKeyDown={handleDraftKeyDown}
                                autoFocus
                            />
                            <div className="floating-comment-actions">
                                <button className="floating-comment-submit" onClick={submitDraft}>
                                    {t('comments.submit')}
                                </button>
                                <button className="floating-comment-cancel" onClick={() => { setDraftText(''); onDraftCancel(); }}>
                                    {t('comments.cancel')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>,
        menu2Container
    ) : null;

    return (
        <>
            {avatarsColumn}
            {expandedPanel}
        </>
    );
};

export default FloatingCommentsPanel;
